import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { formatDate } from "@/utils/timezone";
import { formatCurrency } from "@/utils/formatters";
import ColoredPercent from "../ColoredPercent";
import ColoredCurrency from "../ColoredCurrency";
import { useNotification } from "../NotificationContext";
import api from "@/api";

const OldTickerDetailsPage = ({ accounts }) => {
  const { accountId, symbol } = useParams();
  const navigate = useNavigate();
  const { showError } = useNotification();

  const [loading, setLoading] = useState(true);
  const [holding, setHolding] = useState(null);
  const [accountCurrent, setAccountCurrent] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [intel, setIntel] = useState([]);
  const [activeTab, setActiveTab] = useState("timeline");

  useEffect(() => {
    if (accountId && symbol) {
      fetchTickerData();
    }
  }, [accountId, symbol]);

  const fetchTickerData = async () => {
    try {
      setLoading(true);

      // Fetch all ticker-related data in parallel
      const [currentResponse, transactionsResponse, intelResponse] =
        await Promise.all([
          api.get(`/accounts/${accountId}/current`),
          api.get(`/accounts/${accountId}/transactions`, {
            params: {
              ticker: symbol,
              action: "ALL",
              page: 1,
              limit: 1000, // Get all transactions for this ticker
            },
          }),
          api.get(`/intel`, {
            params: { ticker: symbol },
          }),
        ]);

      // Find the holding for this ticker
      const tickerHolding = currentResponse.data.holdings.find(
        (h) => h.ticker === symbol
      );
      setHolding(tickerHolding);
      setAccountCurrent(currentResponse.data);

      setTransactions(transactionsResponse.data.transactions || []);
      setIntel(intelResponse.data.items || intelResponse.data || []);
    } catch (error) {
      console.error("Error fetching ticker data:", error);
      showError(`Failed to load ticker data: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Combine all timeline items (transactions + intel)
  const getTimelineItems = () => {
    const items = [];

    // Add transactions
    transactions.forEach((txn) => {
      items.push({
        type: "transaction",
        date: new Date(txn.date),
        data: txn,
      });
    });

    // Add intel
    intel.forEach((item) => {
      items.push({
        type: "intel",
        date: new Date(item.timestamp),
        data: item,
      });
    });

    // Sort by date descending (newest first)
    return items.sort((a, b) => b.date - a.date);
  };

  const renderTransactionRow = (txn) => {
    const isBuy = txn.type === "BUY" || txn.type === "SHARES_IN";
    const isSell = txn.type === "SELL";
    const isDividend = txn.type === "DIVIDEND";

    return (
      <div
        key={`txn-${txn.id}`}
        style={{
          padding: "1rem",
          borderLeft: `4px solid ${
            isBuy ? "#10b981" : isSell ? "#ef4444" : "#6366f1"
          }`,
          backgroundColor: "var(--card-bg)",
          borderRadius: "0.5rem",
          marginBottom: "0.75rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
                marginBottom: "0.5rem",
              }}
            >
              <span
                style={{
                  fontSize: "1.5rem",
                }}
              >
                {isBuy ? "📈" : isSell ? "📉" : "💰"}
              </span>
              <div>
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "1rem",
                    color: "var(--app-text)",
                  }}
                >
                  {txn.type}
                  {txn.shares && ` • ${txn.shares} shares`}
                  {txn.price && ` @ ${formatCurrency(txn.price)}`}
                </div>
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: "var(--subtitle-color)",
                  }}
                >
                  {formatDate(txn.date)}
                </div>
              </div>
            </div>

            {txn.description && (
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "var(--app-text)",
                  marginLeft: "2.5rem",
                }}
              >
                {txn.description}
              </div>
            )}
          </div>

          <div style={{ textAlign: "right" }}>
            {txn.amount_reported && (
              <div
                style={{
                  fontWeight: "600",
                  fontSize: "1.125rem",
                  color: isBuy
                    ? "#ef4444"
                    : isSell || isDividend
                    ? "#10b981"
                    : "var(--app-text)",
                }}
              >
                {isBuy ? "-" : "+"}
                {formatCurrency(Math.abs(txn.amount_reported))}
              </div>
            )}
            {txn.fees > 0 && (
              <div
                style={{
                  fontSize: "0.75rem",
                  color: "var(--subtitle-color)",
                }}
              >
                Fees: {formatCurrency(txn.fees)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderIntelItem = (item) => {
    return (
      <div
        key={`intel-${item.id}`}
        style={{
          padding: "1rem",
          borderLeft: "4px solid #8b5cf6",
          backgroundColor: "var(--card-bg)",
          borderRadius: "0.5rem",
          marginBottom: "0.75rem",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "0.75rem",
          }}
        >
          <span style={{ fontSize: "1.5rem" }}>📸</span>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: "0.5rem",
              }}
            >
              <div>
                <div
                  style={{
                    fontWeight: "600",
                    fontSize: "1rem",
                    color: "var(--app-text)",
                  }}
                >
                  Intel
                  {item.platform && ` • ${item.platform}`}
                  {item.author && ` • ${item.author}`}
                </div>
                <div
                  style={{
                    fontSize: "0.875rem",
                    color: "var(--subtitle-color)",
                  }}
                >
                  {formatDate(item.timestamp)}
                </div>
              </div>
            </div>

            {item.thesis && (
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "var(--app-text)",
                  marginBottom: "0.5rem",
                  padding: "0.5rem",
                  backgroundColor: "var(--secondary)",
                  borderRadius: "0.25rem",
                }}
              >
                <strong>Thesis:</strong> {item.thesis}
              </div>
            )}

            {item.text_content && (
              <div
                style={{
                  fontSize: "0.875rem",
                  color: "var(--app-text)",
                  whiteSpace: "pre-wrap",
                }}
              >
                {item.text_content}
              </div>
            )}

            {item.screenshot_data && (
              <div style={{ marginTop: "0.5rem" }}>
                <img
                  src={item.screenshot_data}
                  alt="Intel screenshot"
                  style={{
                    maxWidth: "100%",
                    borderRadius: "0.25rem",
                    border: "1px solid var(--border)",
                  }}
                />
              </div>
            )}

            {item.tags && item.tags.length > 0 && (
              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  marginTop: "0.5rem",
                  flexWrap: "wrap",
                }}
              >
                {item.tags.map((tag, idx) => (
                  <span
                    key={idx}
                    style={{
                      fontSize: "0.75rem",
                      padding: "0.25rem 0.5rem",
                      backgroundColor: "var(--accent)",
                      borderRadius: "0.25rem",
                      color: "var(--accent-foreground)",
                    }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        Loading ticker data...
      </div>
    );
  }
  const account = accountCurrent.account;
  const timelineItems = getTimelineItems();
  const totalTransactions = transactions.length;
  const totalIntel = intel.length;
  const buyTransactions = transactions.filter(
    (t) => t.type === "BUY" || t.type === "SHARES_IN"
  );
  const sellTransactions = transactions.filter((t) => t.type === "SELL");
  const dividendTransactions = transactions.filter(
    (t) => t.type === "DIVIDEND"
  );

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div>
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/old/account/${accountId}`)}
            style={{ marginBottom: "1rem" }}
          >
            ← Back to {account.name}
          </button>
          <h1 className="dashboard-title">{symbol}</h1>
          <p className="dashboard-subtitle">
            {holding
              ? `Currently holding ${holding.shares} shares`
              : "No current position"}
          </p>
        </div>
      </div>

      {/* Ticker Summary Cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "1rem",
          marginBottom: "2rem",
        }}
      >
        {holding && (
          <>
            <div className="account-card">
              <div className="stat-label">Current Price</div>
              <div className="stat-value" style={{ fontSize: "1.5rem" }}>
                {formatCurrency(holding.current_price || 0)}
              </div>
              <div
                className="text-muted"
                style={{ fontSize: "0.75rem", marginTop: "0.25rem" }}
              >
                Entry: {formatCurrency(holding.avg_cost_basis || 0)}
              </div>
            </div>

            <div className="account-card">
              <div className="stat-label">Market Value</div>
              <div className="stat-value" style={{ fontSize: "1.5rem" }}>
                {formatCurrency(holding.market_value || 0)}
              </div>
              <div style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}>
                <span className="text-muted">Shares: </span>
                {holding.shares}
              </div>
            </div>

            <div className="account-card">
              <div className="stat-label">Unrealized P&L</div>
              <div className="stat-value" style={{ fontSize: "1.5rem" }}>
                <ColoredCurrency value={holding.unrealized_gain_loss || 0} />
              </div>
              <div style={{ fontSize: "0.875rem", marginTop: "0.25rem" }}>
                <ColoredPercent
                  value={
                    holding.avg_cost_basis
                      ? ((holding.unrealized_gain_loss || 0) /
                          (holding.shares * holding.avg_cost_basis)) *
                        100
                      : 0
                  }
                />
              </div>
            </div>
          </>
        )}

        <div className="account-card">
          <div className="stat-label">Activity</div>
          <div className="stat-value" style={{ fontSize: "1.5rem" }}>
            {totalTransactions + totalIntel}
          </div>
          <div
            className="text-muted"
            style={{ fontSize: "0.75rem", marginTop: "0.25rem" }}
          >
            {totalTransactions} transactions • {totalIntel} intel
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: "2rem" }}>
        <div
          className="border-subtle"
          style={{ display: "flex", borderBottom: "1px solid" }}
        >
          <button
            className={`tab-button ${activeTab === "timeline" ? "active" : ""}`}
            onClick={() => setActiveTab("timeline")}
            style={{
              padding: "1rem 2rem",
              fontWeight: activeTab === "timeline" ? "600" : "500",
            }}
          >
            📊 Full History
          </button>
          <button
            className={`tab-button ${
              activeTab === "transactions" ? "active" : ""
            }`}
            onClick={() => setActiveTab("transactions")}
            style={{
              padding: "1rem 2rem",
              fontWeight: activeTab === "transactions" ? "600" : "500",
            }}
          >
            💱 Transactions ({totalTransactions})
          </button>
          <button
            className={`tab-button ${activeTab === "intel" ? "active" : ""}`}
            onClick={() => setActiveTab("intel")}
            style={{
              padding: "1rem 2rem",
              fontWeight: activeTab === "intel" ? "600" : "500",
            }}
          >
            📸 Intel ({totalIntel})
          </button>
        </div>

        {/* Tab Content */}
        <div style={{ marginTop: "1.5rem", minHeight: "400px" }}>
          {activeTab === "timeline" && (
            <div>
              <div className="section-content">
                <h3 className="section-title">Complete Timeline</h3>
                <p
                  className="section-description"
                  style={{ marginBottom: "1.5rem" }}
                >
                  All transactions, intel, and activity for {symbol} in
                  chronological order
                </p>
              </div>

              {timelineItems.length === 0 ? (
                <div className="empty-state">
                  <h3>No activity yet</h3>
                  <p>No transactions or intel found for {symbol}</p>
                </div>
              ) : (
                <div>
                  {timelineItems.map((item) =>
                    item.type === "transaction"
                      ? renderTransactionRow(item.data)
                      : renderIntelItem(item.data)
                  )}
                </div>
              )}
            </div>
          )}

          {activeTab === "transactions" && (
            <div>
              <div className="section-content">
                <h3 className="section-title">Transactions</h3>
                <p
                  className="section-description"
                  style={{ marginBottom: "1.5rem" }}
                >
                  {buyTransactions.length} buys • {sellTransactions.length}{" "}
                  sells • {dividendTransactions.length} dividends
                </p>
              </div>

              {transactions.length === 0 ? (
                <div className="empty-state">
                  <h3>No transactions yet</h3>
                  <p>No transactions found for {symbol}</p>
                </div>
              ) : (
                <div>
                  {transactions.map((txn) => renderTransactionRow(txn))}
                </div>
              )}
            </div>
          )}

          {activeTab === "intel" && (
            <div>
              <div className="section-content">
                <h3 className="section-title">Intel</h3>
                <p
                  className="section-description"
                  style={{ marginBottom: "1.5rem" }}
                >
                  Research, screenshots, and notes for {symbol}
                </p>
              </div>

              {intel.length === 0 ? (
                <div className="empty-state">
                  <h3>No intel yet</h3>
                  <p>No intel found for {symbol}</p>
                </div>
              ) : (
                <div>{intel.map((item) => renderIntelItem(item))}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OldTickerDetailsPage;
